<template>
  <div>
    <div class="measurements">
      <div class="row">
        <p>Radius: &nbsp;</p>

        <div class="input-wrapper">
          <div class="input-group">
            <input
              v-model.number="radius"
              class="radius-input outlined-input"
              data-testid="radius-input"
              type="number"
              @input="handleRadiusInput"
              @blur="formatRadius"
              @focus="inputFocused = true; $event.target.select()"
              @mouseup.prevent
            >
          </div>
          <TabToggle
            v-model="unit"
            :options="unitOptions"
            class="mr-2"
            data-testid="unit-toggle"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {circleRadius, setEditable, setRadius, feetToMeters, toFeet, toMiles, toMeters} from '@/lib/toolbox/circleMode';
import {watch} from 'vue';
import TabToggle from '@/components/Reusable/TabToggle.vue';

export default {
  name: 'RadiusBox',
  components: {TabToggle},
  data() {
    const unit = 'ft';
    const radius = circleRadius.value ? toFeet(circleRadius.value).toFixed(2) : '';
    return {
      radius,
      previousRadius: radius,
      inputFocused: false,
      unit,
      unitOptions: [
        {value: 'ft', label: 'ft'},
        {value: 'mile', label: 'mile'},
      ],
    };
  },
  computed: {
    inputWidth() {
      const value = String(this.radius);
      const length = value.length || 1;
      const minWidth = 50;
      const charWidth = 10;
      const padding = 16;
      return `${Math.max(minWidth, length * charWidth + padding)}px`;
    },
  },
  beforeMount() {
    // Helper converters to keep conversion logic in one place.
    const convertMetersToDisplay = (meters, targetUnit) =>
      targetUnit === 'ft' ? toFeet(meters) : toMiles(meters);

    const convertDisplayToMeters = (value, fromUnit) =>
      fromUnit === 'ft' ? feetToMeters(value) : toMeters(value);

    // Watch unit changes and convert the currently-displayed value
    // using meters as the canonical intermediate.
    watch(() => this.unit, (newUnit, oldUnit) => {
      if (newUnit === oldUnit || this.inputFocused) return;

      const currentValue = Number(this.radius);
      if (isNaN(currentValue)) return;

      const meters = convertDisplayToMeters(currentValue, oldUnit);
      const converted = convertMetersToDisplay(meters, newUnit);

      this.radius = Number(converted).toFixed(2);
      this.previousRadius = this.radius;
      setRadius(meters);
    });

    // Keep the displayed radius in sync with the reactive circleRadius (meters).
    watch(() => circleRadius.value, (newVal) => {
      if (newVal == null || this.inputFocused) return;
      const convertedValue = convertMetersToDisplay(newVal, this.unit);
      this.radius = convertedValue.toFixed(2);
      this.previousRadius = this.radius;
    });

    setEditable(true);
  },
  beforeUnmount() {
    setEditable(false);
  },
  methods: {
    handleRadiusInput() {
      const numberValue = Number(this.radius);
      if (isNaN(numberValue) || numberValue < 0) return;

      const meters = this.unit === 'ft' ? feetToMeters(numberValue) : toMeters(numberValue);
      setRadius(meters);
    },
    formatRadius() {
      this.inputFocused = false;
      const numberValue = Number(this.radius);
      if (isNaN(numberValue) || numberValue < 0) {
        this.radius = this.previousRadius;
        const meters = this.unit === 'ft' ? feetToMeters(this.previousRadius) : toMeters(this.previousRadius);
        setRadius(meters);
      }
      this.previousRadius = this.radius;
      this.radius = numberValue.toFixed(2);
    },
  },
};
</script>

<style scoped lang="scss">
.measurements {
  width: 100%;
  min-width: 200px;

  .row {
    height: 56px;
    display: flex;
    flex-direction: row;
    align-items: center;

    p {
      color: main.$npGray;
      margin: 0 8px 0 0;
    }

    .input-wrapper {
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }
}

.input-group {
  display: flex;
  align-items: center;
}

.radius-input {
  height: 30px;
  width: 80px;
  min-width: 50px;
  max-width: 150px;
  border-radius: 4px;
  transition: width 0.2s ease;
  padding: 4px 8px;
  border: 1px solid #ccc;
  outline: none;
}

</style>
